import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type QueryParams } from '@/lib/api/client';
import { leadsApi, type LeadTypeKey } from './api';
import { LeadDetailDialog } from './LeadDetailDialog';
import { NEWSLETTER_STATUSES } from './fields';
import { getLeadType } from '@/config/leadTypes';
import { keys } from '@/lib/query/keys';
import { useListState } from '@/lib/hooks/useListState';
import { useDebounced } from '@/lib/hooks/useDebounced';
import { useGate } from '@/lib/rbac/Gate';
import { useApiError } from '@/lib/query/useApiError';
import { isAppError } from '@/lib/api/errors';
import type { LeadStatus, NewsletterStatus, User } from '@/lib/api/types';
import { EXPORT_ROW_CAP } from '@/lib/config';
import { DataView } from '@/lib/ui/states';
import { DataTable, type Column } from '@/lib/ui/Table';
import { Pagination } from '@/lib/ui/Pagination';
import { PageHeader, MonoValue } from '@/lib/ui/Typography';
import { LeadStatusPill, NewsletterStatusPill } from '@/lib/ui/StatusPill';
import { Tag } from '@/lib/ui/Tag';
import { PermissionButton } from '@/lib/ui/Button';
import { Select, type SelectOption } from '@/lib/ui/Select';
import { TextInput } from '@/lib/ui/Field';
import { Switch } from '@/lib/ui/Switch';
import { useToast } from '@/lib/ui';
import { formatRelative } from '@/lib/format';

type Row = Record<string, unknown>;

/** Read a string-ish field off a heterogeneous lead row. */
function str(row: Row, key: string): string {
  const v = row[key];
  return v == null ? '' : String(v);
}

function statusLabel(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
}

/** Triggers a browser download of a blob with the given filename. */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Config-driven list for any of the four lead types (spec §11). Route: /leads/:type. */
export function LeadsPage() {
  const { t } = useTranslation();
  const { type = '' } = useParams();
  const cfg = getLeadType(type);
  const leadType = type as LeadTypeKey;
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const onError = useApiError();

  const { allowed: canRead } = useGate('leads:read');

  // Seed the status filter from the URL (dashboard deep-links, e.g. ?status=new).
  const initialStatus = searchParams.get('status') ?? '';
  const list = useListState({ status: initialStatus });
  const [rawSearch, setRawSearch] = useState('');
  const debounced = useDebounced(rawSearch, 300);
  const [spam, setSpam] = useState(false);
  const [active, setActive] = useState<Row | null>(null);

  const isNewsletter = leadType === 'newsletter';
  const showSpamControls = !isNewsletter;

  // Admin users for the assignee filter + manage picker. Tolerate 403 by hiding.
  const usersQuery = useQuery({
    queryKey: keys.users.list({ per_page: 100 }),
    queryFn: () => api.get<User[]>('/admin/users', { per_page: 100 }),
    enabled: !!cfg && canRead && !isNewsletter,
    retry: false,
  });
  const usersForbidden = isAppError(usersQuery.error) && usersQuery.error.is('forbidden');
  const users: User[] = usersForbidden ? [] : (usersQuery.data ?? []);

  // Effective status: the Spam toggle wins over the status select for non-newsletter types.
  const selectedStatus = (list.filters.status as string) || '';
  const effectiveStatus = showSpamControls && spam ? 'spam' : selectedStatus;

  const queryParams = useMemo<QueryParams>(() => {
    const p: QueryParams = { ...list.params, q: debounced || undefined };
    if (showSpamControls) {
      // Spam hidden by default: only show when the Spam toggle is on.
      p.status = spam ? 'spam' : selectedStatus || undefined;
    }
    return p;
  }, [list.params, debounced, spam, showSpamControls, selectedStatus]);

  const query = useQuery({
    queryKey: keys.leads.list(leadType, queryParams),
    queryFn: () => leadsApi.list(leadType, queryParams),
    placeholderData: (prev) => prev,
    enabled: !!cfg,
  });

  const [exporting, setExporting] = useState(false);
  async function onExport() {
    setExporting(true);
    try {
      const res = await leadsApi.exportCsv(leadType, queryParams);
      downloadBlob(res.data, `leads-${leadType}.csv`);
      if (res.headers.get('X-Export-Truncated') === 'true') {
        toast.warning(
          t('leads.exportTruncatedTitle'),
          t('leads.exportTruncatedBody', { count: EXPORT_ROW_CAP }),
        );
      }
    } catch (err) {
      onError(err);
    } finally {
      setExporting(false);
    }
  }

  if (!cfg) return <p className="text-small text-ink-mute">{t('leads.unknownType')}</p>;

  // ─── Filters: status options ───────────────────────────────────────────────
  const statusOptions: SelectOption[] = isNewsletter
    ? [
        { value: '', label: t('leads.allStatuses') },
        ...NEWSLETTER_STATUSES.map((s) => ({ value: s, label: t(`leadStatus.${s}`) })),
      ]
    : [
        { value: '', label: t('leads.allStatuses') },
        ...cfg.statuses
          .filter((s) => s !== 'spam')
          .map((s) => ({ value: s, label: t(`leadStatus.${s}`) })),
      ];

  const assigneeOptions: SelectOption[] = [
    { value: '', label: t('leads.allAssignees') },
    ...users.map((u) => ({ value: u.id, label: u.name })),
  ];

  // ─── Columns ────────────────────────────────────────────────────────────────
  const columns: Column<Row>[] = buildColumns(leadType, users, t);

  // Newsletter confirmed/pending counts surfaced from the current page meta is not reliable;
  // show the header total and a per-page breakdown chip set instead.
  const pageRows = (query.data?.data ?? []) as unknown as Row[];
  const confirmedCount = pageRows.filter((r) => r.status === 'confirmed').length;
  const pendingCount = pageRows.filter((r) => r.status === 'pending_confirmation').length;

  return (
    <div>
      <PageHeader
        eyebrow={t(`nav.${cfg.navKey}`).toUpperCase()}
        title={t(`nav.${cfg.navKey}`)}
        count={query.data?.meta?.pagination.total}
        actions={
          cfg.hasExport ? (
            <PermissionButton
              allowed={canRead}
              reason={t('leads.readPermissionReason')}
              variant="ghost"
              loading={exporting}
              onClick={onExport}
            >
              {t('leads.exportCsv')}
            </PermissionButton>
          ) : undefined
        }
      />

      {isNewsletter && pageRows.length > 0 && (
        <div className="mb-md flex flex-wrap items-center gap-sm">
          <Tag variant="teal">{t('leads.confirmedPage', { count: confirmedCount })}</Tag>
          <Tag variant="amber">{t('leads.pendingPage', { count: pendingCount })}</Tag>
        </div>
      )}

      {/* Filters */}
      <div className="mb-md flex flex-wrap items-end gap-sm">
        <div className="w-[200px]">
          <Select
            label={t('common.status')}
            value={(list.filters.status as string) || ''}
            onValueChange={(v) => list.setFilter('status', v || undefined)}
            options={statusOptions}
            disabled={showSpamControls && spam}
          />
        </div>

        {!isNewsletter && !usersForbidden && (
          <div className="w-[200px]">
            <Select
              label={t('leads.assignee')}
              value={(list.filters.assigned_to as string) || ''}
              onValueChange={(v) => list.setFilter('assigned_to', v || undefined)}
              options={assigneeOptions}
            />
          </div>
        )}

        <div className="w-[160px]">
          <TextInput
            label={t('leads.from')}
            type="date"
            value={(list.filters.from as string) || ''}
            onChange={(e) => list.setFilter('from', e.target.value || undefined)}
          />
        </div>
        <div className="w-[160px]">
          <TextInput
            label={t('leads.to')}
            type="date"
            value={(list.filters.to as string) || ''}
            onChange={(e) => list.setFilter('to', e.target.value || undefined)}
          />
        </div>

        {!isNewsletter && (
          <div className="pb-[6px]">
            <Switch
              label={t('leads.newOnly')}
              checked={(list.filters.status as string) === 'new'}
              onCheckedChange={(v) => list.setFilter('status', v ? 'new' : undefined)}
            />
          </div>
        )}

        {showSpamControls && (
          <div className="pb-[6px]">
            <Switch label={t('leads.spam')} checked={spam} onCheckedChange={(v) => setSpam(v)} />
          </div>
        )}

        <div className="min-w-[180px] flex-1">
          <TextInput
            label={t('common.search')}
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
          />
        </div>
      </div>

      {effectiveStatus === 'spam' && (
        <p className="mb-sm text-small text-ink-mute">{t('leads.showingSpam')}</p>
      )}

      <DataView query={query} isEmpty={(d) => d.data.length === 0}>
        {(d) => (
          <>
            <DataTable
              columns={columns}
              rows={d.data as unknown as Row[]}
              rowKey={(r) => str(r, 'id')}
              onRowClick={(r) => setActive(r)}
            />
            {d.meta?.pagination && (
              <Pagination
                meta={d.meta.pagination}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
              />
            )}
          </>
        )}
      </DataView>

      {active && (
        <LeadDetailDialog
          type={leadType}
          cfg={cfg}
          row={active}
          users={users}
          open={!!active}
          onOpenChange={(open) => {
            if (!open) setActive(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function buildColumns(
  type: LeadTypeKey,
  users: User[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): Column<Row>[] {
  const assigneeName = (id: string): string =>
    (id && users.find((u) => u.id === id)?.name) || id || '—';

  const created: Column<Row> = {
    id: 'created',
    header: t('common.created'),
    align: 'end',
    cell: (r) => <span className="text-ink-mute">{formatRelative(str(r, 'created_at'))}</span>,
  };

  if (type === 'newsletter') {
    return [
      {
        id: 'email',
        header: t('leads.colEmail'),
        cell: (r) => <span className="font-medium text-ink-deep">{str(r, 'email')}</span>,
      },
      {
        id: 'status',
        header: t('common.status'),
        cell: (r) => <NewsletterStatusPill status={r.status as NewsletterStatus} />,
      },
      {
        id: 'locale',
        header: t('leads.colLocale'),
        cell: (r) => <MonoValue className="text-ink-mid">{str(r, 'locale')}</MonoValue>,
      },
      created,
    ];
  }

  const nameCol: Column<Row> = {
    id: 'name',
    header: t('leads.colName'),
    cell: (r) => (
      <div>
        <div className="font-medium text-ink-deep">{str(r, 'full_name') || '—'}</div>
        <div className="text-ink-mute">{str(r, 'email')}</div>
      </div>
    ),
  };
  const statusCol: Column<Row> = {
    id: 'status',
    header: t('common.status'),
    cell: (r) => <LeadStatusPill status={r.status as LeadStatus} />,
  };
  const assigneeCol: Column<Row> = {
    id: 'assignee',
    header: t('leads.assignee'),
    cell: (r) => <span className="text-ink-mid">{assigneeName(str(r, 'assigned_to'))}</span>,
  };

  if (type === 'contact') {
    return [
      nameCol,
      {
        id: 'company',
        header: t('leads.colCompany'),
        cell: (r) => <span className="text-ink-mid">{str(r, 'company') || '—'}</span>,
      },
      {
        id: 'inquiry',
        header: t('leads.colInquiry'),
        cell: (r) => {
          const it = str(r, 'inquiry_type');
          return it ? (
            <Tag variant="teal">{statusLabel(it)}</Tag>
          ) : (
            <span className="text-ink-mute">—</span>
          );
        },
      },
      statusCol,
      assigneeCol,
      created,
    ];
  }

  if (type === 'applications') {
    return [
      nameCol,
      {
        id: 'cv',
        header: t('leads.colCv'),
        cell: (r) =>
          r.resume_media_id ? (
            <Tag variant="teal">{t('leads.cvYes')}</Tag>
          ) : (
            <Tag variant="amber">{t('leads.cvNo')}</Tag>
          ),
      },
      statusCol,
      assigneeCol,
      created,
    ];
  }

  // quotes
  return [
    nameCol,
    {
      id: 'company',
      header: t('leads.colCompany'),
      cell: (r) => <span className="text-ink-mid">{str(r, 'company') || '—'}</span>,
    },
    statusCol,
    assigneeCol,
    created,
  ];
}
